/**
 * Etapa 4, ajustes 4d (2026-10-05), punto 5: los textos fijos del dibujo de la red en el Registro Público de Comercio, compartidos por
 * Argus Team (components/RedRegistro.tsx) y Nexo (ui/GrafoSiger.tsx). Lo que dicen los nodos está en ../piezas/red.ts (NODO_RED) y la
 * identidad frente a OFAC en ../piezas/red-senales.ts.
 *
 * Estado de aprobación: `DE_NEXO` son los textos que el grafo de Nexo ya mostraba (su leyenda la aprobó el coordinador por delegación el
 * 2026-10-04; los demás vienen de la etapa 1b); `RED` son textos nuevos de Argus Team, POR APROBAR, cada uno con el patrón aprobado que
 * sigue (bitácora 2026-10-03-etapa4-piezas.md, «4d · red en Argus Team: implementación»).
 */
export const RED = {
  /** Caja propia de la red (Bernardo, 2026-10-05, «Caja propia, cerrada», aprobados en la vista previa que eligió): título, qué es y el
   * botón que la abre. Antes era un enlace cerrado al final de «Cómo se relaciona» y no se veía. */
  titulo: 'La red en el Registro Público de Comercio',
  queEs: 'Quién más aparece en sus actas y quién de ellos está en la lista de OFAC.',
  ver: 'Ver la red',
  /** El mismo botón con la red abierta. POR APROBAR, patrón «Ocultar la ficha» (FICHA.ocultarFicha). */
  ocultar: 'Ocultar la red',
  /** Patrón «Cargando la ficha…». */
  cargando: 'Cargando la red…',
  /** Encima del dibujo: de quién es la red (la raíz). */
  redDe: 'La red de {nombre} en el Registro Público de Comercio.',
  /** La raíz es alguien con quien la persona del registro puede coincidir sin corroborar (patrón «Puede ser [nombre en la lista]…» del
   * punto 2 de los ajustes 4d y «(sin confirmar)»). */
  puedeSer: 'Puede ser {nombre}, de la lista de OFAC (sin confirmar).',
  /** Revisión I3: la raíz salió de la lista. */
  puedeSerRetirada: 'Puede ser {nombre}, que estuvo en la lista de OFAC (sin confirmar).',
  /** La persona del registro tiene que ver con más de una persona o empresa de la lista: elegir de quién ver la red (patrón «Ver la …»). */
  verRedDe: 'Ver la red de {nombre}',
  /** Sin raíz o sin nada que dibujar. */
  sinRed: 'En las actas que leímos no encontramos relaciones con la lista de OFAC.',
  /** Nada se recorta en silencio (regla de Nexo): las citas se acortaron para que la red quepa. */
  citasCortas: 'Las citas se acortaron para que el dibujo quepa.',
  /** La raíz: según su estado en la lista (los dos primeros ya aprobados como rótulos; el tercero es el texto de la base sin punto). */
  raizEnLista: 'En la lista de OFAC',
  raizRetirada: 'Estuvo en la lista de OFAC',
  raizNoEsta: 'No está en la lista de OFAC',
  /** La identidad de una persona del dibujo frente a una de la lista: el rótulo del nodo, con quién y por qué. */
  identidad: '{rotulo} · {nombre}: {base}.',
  /** Detalle del nodo elegido. */
  documentosDe: 'Aparece en los documentos de {n} empresas.',
  documentosDeUna: 'Aparece en los documentos de 1 empresa.',
} as const;

/** Textos que el grafo de Nexo ya mostraba; Argus Team los muestra igual. */
export const DE_NEXO = {
  muestra: 'El dibujo muestra {n} de {m} relaciones, las de más peso.',
  soloVigentes: 'solo relaciones vigentes según el registro',
  comoLeer: 'Cómo leer este grafo',
  pulsa: 'Pulsa una persona o empresa para ver cómo se relaciona, su identidad y la cita del acta.',
  lejanos: '{n} relaciones más sin intermediario visible en el dibujo con este filtro: se listan abajo.',
  quienEs: '¿Quién es en el Registro Público de Comercio?',
  enElSat: 'En el SAT:',
  comoSeRelaciona: 'Cómo se relaciona',
  listasSat: 'Listas del SAT',
  identidadActas: 'Identidad en las actas',
  sinIdentidadActas: 'Sin datos de identidad en las actas leídas.',
  noCoincide: 'No coincide con nadie en la lista de OFAC.',
  citaNoVerificada: '(cita no verificada)',
  posteriorSancion: 'vigente o posterior a la sanción de OFAC',
  pctSancionados: 'según la última lista de socios, sancionados tenían {n} % de la sociedad',
} as const;

/** La leyenda del dibujo (aprobada por delegación el 2026-10-04; «posible», la marca del buscador aprobada el mismo día). */
export const LEYENDA_RED = {
  relacion: 'Relación:',
  rojo: 'capital, cargo, poder o familia con un sancionado',
  ambar: 'otra relación directa, o a través de una persona o empresa',
  gris: 'contexto: red del sancionado relacionado, a través de varias personas o empresas, autoridad',
  terminado: 'cargo terminado o sustituido',
  persona: 'persona',
  empresa: 'empresa',
  identidad: 'Identidad:',
  enLista: 'confirmada; cómo se identificó, al pulsar el nodo',
  quizaEnLista: 'sin confirmar (puede ser otra persona con el mismo nombre)',
  contradice: 'un dato lo contradice',
  cincuenta: 'según la última lista de socios',
  sat: 'listas del SAT por RFC o denominación',
  satPosible: 'posible (raíz de RFC + nombre), falta confirmar',
  punteado: 'recuadro punteado: relación que declara OFAC sin acta en el registro',
  /** Revisión I3 (por aprobar): quien salió de la lista. */
  estuvo: 'salió de la lista',
} as const;
